import { FEDERAL_TAX_BRACKETS_SINGLE, STATE_TAX_BRACKETS_SINGLE, TaxBracket } from './taxBrackets';
import { STATE_MUNI } from './stateMuni';

// Methodology: Reserve TEY workbook ("Reserve TEY tables.xlsx", Aug 2026).
// - Federal marginal rate is taken at TAXABLE income (assumed income minus the
//   2026 single standard deduction). State rates are taken at assumed income.
// - The 3.8% NIIT applies to Treasury (SGOV) and ordinary savings interest for
//   MAGI >= $200k (single); municipal (SUB) income is excluded from NIIT.
// - SGOV is state/local-tax exempt everywhere. SUB is federal-tax exempt; each
//   state taxes the out-of-state-sourced share per STATE_MUNI.
// - Routing picks whichever fund nets more after tax, per state and bracket.

export type IncomeBracket = 'UNDER_100K' | 'FROM_100K_TO_250K' | 'FROM_250K_TO_500K' | 'OVER_500K';

export const INCOME_BRACKETS: { value: IncomeBracket; label: string }[] = [
  { value: 'UNDER_100K', label: 'Under $100k' },
  { value: 'FROM_100K_TO_250K', label: '$100k – $250k' },
  { value: 'FROM_250K_TO_500K', label: '$250k – $500k' },
  { value: 'OVER_500K', label: '$500k+' },
];

const ASSUMED_INCOME_BY_BRACKET: Record<IncomeBracket, number> = {
  UNDER_100K: 75_000,
  FROM_100K_TO_250K: 175_000,
  FROM_250K_TO_500K: 375_000,
  OVER_500K: 750_000,
};

// 30-day SEC yields (fraction), from the workbook's Inputs tab (as of 2026-08-12).
// Refreshed by the monthly audit.
const SEC_YIELDS: Record<'SGOV' | 'SUB', number> = {
  SGOV: 0.036,
  SUB: 0.0266,
};

const STANDARD_DEDUCTION_SINGLE = 16_100; // 2026
const NIIT_RATE = 0.038;
const NIIT_MAGI_THRESHOLD_SINGLE = 200_000;

export const RESERVE_IRA_MATCH_BONUS_BPS = 25;
export const RESERVE_WELCOME_BOOST_BPS = 65;

function getMarginalTaxRate(income: number, brackets: TaxBracket[]): number {
  let rate = 0;
  for (const bracket of brackets) {
    if (income >= bracket.floor) {
      rate = bracket.rate;
    } else {
      break;
    }
  }
  return rate;
}

export interface TaxProfile {
  state: string;
  bracket: IncomeBracket;
}

export interface TaxRates {
  federalRate: number; // marginal rate at federal taxable income
  niitRate: number; // 0 or 0.038
  stateRate: number; // marginal rate at assumed income
  combinedRate: number; // federal + NIIT + state (ordinary interest income)
}

export function getTaxRates(profile: TaxProfile): TaxRates {
  const assumedIncome = ASSUMED_INCOME_BY_BRACKET[profile.bracket];
  const federalTaxable = Math.max(0, assumedIncome - STANDARD_DEDUCTION_SINGLE);
  const federalRate = getMarginalTaxRate(federalTaxable, FEDERAL_TAX_BRACKETS_SINGLE);
  const niitRate = assumedIncome > NIIT_MAGI_THRESHOLD_SINGLE ? NIIT_RATE : 0;
  const stateBrackets = STATE_TAX_BRACKETS_SINGLE[profile.state] ?? STATE_TAX_BRACKETS_SINGLE['FL'];
  const stateRate = getMarginalTaxRate(assumedIncome, stateBrackets);
  return { federalRate, niitRate, stateRate, combinedRate: federalRate + niitRate + stateRate };
}

export interface ReserveQuote {
  symbol: 'SGOV' | 'SUB';
  baseBps: number; // SEC yield of the routed fund
  bonusBps: number; // IRA match + (optionally) welcome boost
  preTaxBps: number; // base + bonuses
  teyBps: number; // tax-equivalent yield incl. bonuses
  afterTaxBps: number; // routed fund's after-tax yield, before bonuses
  federalRate: number;
  niitRate: number;
  stateRate: number;
}

export function getReserveQuote(profile: TaxProfile, bonusOn: boolean): ReserveQuote {
  const { federalRate, niitRate, stateRate, combinedRate } = getTaxRates(profile);
  const muni = STATE_MUNI[profile.state] ?? {
    taxesInterest: false,
    outOfStateMuniTaxable: false,
    inStateExempt: false,
    subInStatePct: 0,
  };

  // SGOV: taxed federally (incl. NIIT); exempt from state tax everywhere.
  const sgovAfter = SEC_YIELDS.SGOV * (1 - federalRate - niitRate);
  // SUB: federal-exempt, NIIT-exempt; the state taxes the out-of-state-sourced
  // share, except where the state exempts municipal interest entirely (e.g. DC).
  const subTaxableShare =
    muni.taxesInterest && muni.outOfStateMuniTaxable
      ? muni.inStateExempt
        ? 1 - muni.subInStatePct
        : 1
      : 0;
  const subAfter = SEC_YIELDS.SUB * (1 - stateRate * subTaxableShare);

  const symbol: 'SGOV' | 'SUB' = sgovAfter >= subAfter ? 'SGOV' : 'SUB';
  const afterTax = Math.max(sgovAfter, subAfter);
  const teyBase = combinedRate < 1 ? afterTax / (1 - combinedRate) : 0;

  const bonusBps = RESERVE_IRA_MATCH_BONUS_BPS + (bonusOn ? RESERVE_WELCOME_BOOST_BPS : 0);

  return {
    symbol,
    baseBps: Math.round(SEC_YIELDS[symbol] * 10000),
    bonusBps,
    preTaxBps: Math.round(SEC_YIELDS[symbol] * 10000) + bonusBps,
    teyBps: Math.floor(teyBase * 10000 + 1e-9) + bonusBps,
    afterTaxBps: Math.round(afterTax * 10000),
    federalRate,
    niitRate,
    stateRate,
  };
}

/** After-tax yield of a fully taxable account (an ordinary HYSA; interest bears NIIT too). */
export function getHysaAfterTaxBps(apyBps: number, profile: TaxProfile): number {
  const { combinedRate } = getTaxRates(profile);
  return Math.round(apyBps * (1 - combinedRate));
}

export function formatBps(bps: number): string {
  return (bps / 100).toFixed(2) + '%';
}

export function formatUsd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export type Grade = 'Poor' | 'Below average' | 'Above average' | 'Competitive' | 'Excellent';

export function gradeRate(apyBps: number, marketBps: number[]): { grade: Grade; percentile: number } {
  const beaten = marketBps.filter((b) => apyBps > b).length;
  const percentile = Math.round((beaten / marketBps.length) * 100);
  let grade: Grade = 'Poor';
  if (percentile >= 95) grade = 'Excellent';
  else if (percentile > 80) grade = 'Competitive';
  else if (percentile >= 50) grade = 'Above average';
  else if (percentile >= 25) grade = 'Below average';
  return { grade, percentile };
}
